import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store/rootReducer';

import { Table, TableColumnsType } from 'antd';


import Button from '../../Button';
import Checkbox from '../../Checkbox';
import Radio from '../../Radio';
import ConfirmModal from '../../modals/ConfirmModal';
import useAxios from '../../../hooks/useAxios';
import { IAbsenceRefund, IGetAbsenceRefunds } from '../../../models/campusClasses.model';
import { getAbsenceRefunds } from '../../../api/campusClasses.api';
import Loader from '../../Loader';
import ErrorResponse from '../../ErrorResponse';
import moment from 'moment';
import _ from 'lodash';
import useNotification from '../../../hooks/useNotification';
import { submitAbsenceRefunds } from '../../../api/refunds.api';

interface IProps {
  setTab: (tab: string) => void;
  tabOrigin: string;
}

const ClassRefund: FC<IProps> = ({
  setTab,
  tabOrigin,
}) => {
  const { t } = useTranslation();
  const { chosenClass } = useSelector((state: RootState) => state.class);
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetAbsenceRefunds>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();

  const [attendCodes, setAttendCodes] = useState<number[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [type, setType] = useState<'RE'|'CO'>('RE');
  const [finalAmount, setFinalAmount] = useState(0);
  const [memberKorName, setMemberKorName] = useState('');
  const [memberEngName, setMemberEngName] = useState('');
  const [classStudentCode, setClassStudentCode] = useState('');
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);

  useEffect(() => {
    const _memberKorName = sessionStorage.getItem('memberKorName');
    const _memberEngName = sessionStorage.getItem('memberEngName');
    const _classStudentCode = sessionStorage.getItem('classStudentCode');
    if (_memberKorName) setMemberKorName(_memberKorName);
    if (_memberEngName) setMemberEngName(_memberEngName);
    if (_classStudentCode) setClassStudentCode(_classStudentCode);
  }, []);

  useEffect(() => {
    if (classStudentCode) {
      execute(getAbsenceRefunds(classStudentCode));
    }
  }, [classStudentCode]);

  useEffect(() => {
    if (res && !isLoading) {
      onSelectAll();
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      setConfirmModalVisible(false);
      successNotification(t('common.button.processed'));
      setTab(tabOrigin);
    }
  }, [submitRes, submitIsLoading]);

  const onSelect = (attendCode: number) => {
    if (attendCodes.includes(attendCode)) {
      const _attendCodes = attendCodes.filter(code => code !== attendCode);
      calculateFinalAmount(_attendCodes);
      setAttendCodes(_attendCodes);
      setIsSelectAll(false);
    } else {
      const _attendCodes = [...attendCodes, attendCode];
      calculateFinalAmount(_attendCodes);
      setAttendCodes(_attendCodes);
    }
  };

  const onSelectAll = () => {
    if (isSelectAll) {
      setAttendCodes([]);
      setFinalAmount(0);
      setIsSelectAll(false);
    } else {
      const _attendCodes = _.uniq(res?.data?.map(item => item.attendCode));
      calculateFinalAmount(_attendCodes);
      setAttendCodes(_attendCodes);
      setIsSelectAll(true);
    }
  };

  const calculateFinalAmount = (attendCodes: number[]) => {
    const _finalAmount = res?.data?.reduce((acc, cur) => {
      if (attendCodes.includes(cur.attendCode)) {
        return acc + cur.price;
      }
      return acc;
    }, 0);

    if (_finalAmount !== undefined) setFinalAmount(_finalAmount);
  }

  const onOpenConfirmModal = () => {
    if (attendCodes.length === 0) {      
      return infoNotification(t('common.button.notFound'));
    }

    setConfirmModalVisible(true);
  }

  const onSubmit = () => {
    submitExecute(submitAbsenceRefunds({
      type,
      attendCodes,
      amount: finalAmount
    }));
  }

  const columns: TableColumnsType<IAbsenceRefund> = [
    {
      title: (
        <Checkbox
          checked={isSelectAll}
          onClickCapture={(e: any) => {
            e.stopPropagation();
            onSelectAll();
          }}
          text=""
        />
      ),
      dataIndex: 'attendCode',
      key: 'attendCode',
      width: '10%',
      align: 'center',
      render: value => (
        <Checkbox
          checked={attendCodes.includes(value)}
          onChange={() => onSelect(value)}
          text=""
        />
      )
    },
    {
      title: t('common.column.date'),
      dataIndex: 'firstRegDttm',
      key: 'firstRegDttm',
      width: '30%',
      align: 'center',
      render: value => moment(value).format('YYYY.MM.DD')
    },
    {
      title: t('common.text.note'),
      dataIndex: 'memo',
      key: 'memo',
      width: '30%',
      align: 'center',
    },
    {
      title: t('student.student.column97'),
      dataIndex: 'price',
      key: 'price',
      width: '30%',
      align: 'center',
      render: value => value?.toLocaleString() || 0
    },
  ];

  return (
    <>
      {isLoading || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />

      <div className="comm-bx__between">
        <div>
          <h4 className='sub-title'>{chosenClass?.className}</h4>
        </div>
        <div className="right">
          {memberKorName} ({memberEngName})
        </div>
      </div>

      <div className='comm-table__area'>
        <Table
          columns={columns}
          dataSource={res?.data}
          pagination={{
            pageSize: 99999
          }}
          summary={() => (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell
                  index={0}
                  colSpan={3}
                  className="text-center"
                >
                  {t('student.student.column86')}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={1}
                >
                  <input
                    type="text"
                    className="form-control w-150p"
                    value={finalAmount}
                    onChange={e => setFinalAmount(Number(e.target.value))}
                  />
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          )}
        />
      </div>

      <div className='comm-table__area rowType edit'>
        <table>
          <tbody>
            <tr>
              <th className="text-center">{t('student.student.title18')}</th>
              <td>
                <Radio
                  checked={type === 'RE'}
                  onChange={() => setType('RE')}
                  text={t('student.student.button21')}
                />
                <Radio
                  className="ml-4"
                  checked={type === 'CO'}
                  onChange={() => setType('CO')}
                  text={t('student.student.tab6')}
                />
              </td>
            </tr>
            <tr>
              <th className="text-center align-middle">{t('student.student.column12')}</th>
              <td>
                <textarea
                  rows={3}
                />              
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() => setTab(tabOrigin)}
            text={t('common.button.back')}
          />
        </div>
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            onClick={onOpenConfirmModal}
            text={t('student.student.button21')}
          />
        </div>
      </div>

     <ConfirmModal
        visible={confirmModalVisible}
        setVisible={setConfirmModalVisible}
        onConfirm={onSubmit}
        titleText={t('student.student.button22')}
        messageText={
          (type === 'RE'
            ? `${t('student.student.text21')} ${finalAmount.toLocaleString()}${t('student.student.text22')}`
            : `${t('student.student.text23')}${finalAmount.toLocaleString()}${t('student.student.text24')}`)
          + ` ${t('student.student.text25')}`
        }
        confirmText={t('common.button.confirm')}
        cancelText={t('common.button.cancel')}
      />
    </>
  );
}

export default ClassRefund;
